// Structure and Tailwind classNames ported from the Lovable reference
// implementation (files/snapdraw-design-kit/code/src/routes/forgot-password.tsx),
// built on the shared AuthLayout. Retrofitted with the same real
// supabase.auth.resetPasswordForEmail call as the old AuthPage's forgot mode.
// Field validation (required/invalid-email) ported from the kit's
// 2026-08-22 "form validation & error states" update — this is the exact
// low-contrast "Password recovery requires an email" error a real user
// flagged as hard to read; FieldError (destructive-tinted background, left
// border accent, alert icon, role="alert") replaces the old bare
// destructive-text paragraph. The real resetPasswordForEmail failure case
// reuses the same treatment for consistency.
// Errors store i18n KEYS (not resolved strings) and re-resolve via t() at
// render time, so they stay correct if the user flips the language toggle
// after an error appears (2026-08-22 fix). The real Supabase failure case is
// genuinely untranslatable third-party text (see Message's `raw` case in
// @/lib/i18n/message) — it can't follow the toggle because no Spanish
// version of it exists anywhere in our system.
import { useState } from "react";
import { Link } from "react-router-dom";
import { AtSign } from "lucide-react";

import { AuthLayout, authFieldClassName, authPrimaryClass } from "@/components/snapdraw/auth-layout";
import { FieldError } from "@/components/snapdraw/form";
import { supabase } from "@/lib/supabase";
import { useT } from "@/lib/i18n";
import { resolveMessage, type Message } from "@/lib/i18n/message";
import { cn } from "@/lib/utils";
import "@/styles/tailwind-preview.css";

export function ForgotPasswordPage() {
  const t = useT();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [fieldErrorKey, setFieldErrorKey] = useState("");
  const [submitError, setSubmitError] = useState<Message | null>(null);
  const [busy, setBusy] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitError(null);

    if (!email.trim()) {
      setFieldErrorKey("forgot.error.required");
      return;
    }
    if (!email.includes("@")) {
      setFieldErrorKey("auth.error.email");
      return;
    }
    setFieldErrorKey("");

    setBusy(true);
    try {
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      if (resetError) throw resetError;
      setSent(true);
    } catch (err) {
      setSubmitError(err instanceof Error ? { raw: err.message } : { key: "auth.error.generic" });
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthLayout
      eyebrow={t("forgot.eyebrow")}
      footer={
        <p className="mt-6 text-center text-sm text-foreground/80">
          {t("forgot.remembered")}
          <Link to="/login" className="ml-1.5 text-arc hover:text-spark">
            {t("forgot.backToLogin")}
          </Link>
        </p>
      }
    >
      {sent ? (
        <div className="mt-6 rounded-xl border border-arc/50 bg-black/30 p-6">
          <p className="font-display text-2xl uppercase tracking-wide text-arc">{t("forgot.sentTitle")}</p>
          <p className="mt-3 text-sm text-foreground/80">
            {t("forgot.sentBody", { email: email || t("forgot.sentFallback") })}
          </p>
          <button type="button" onClick={() => setSent(false)} className="mt-5 text-sm text-arc hover:text-spark">
            {t("forgot.useDifferent")}
          </button>
        </div>
      ) : (
        <form className="mt-6 space-y-4" onSubmit={handleSubmit} noValidate>
          {submitError && <FieldError>{resolveMessage(t, submitError)}</FieldError>}

          <div className="space-y-2">
            <label className="relative flex items-center">
              <AtSign className="pointer-events-none absolute left-4 h-4 w-4 text-arc" />
              <input
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (fieldErrorKey) setFieldErrorKey("");
                }}
                placeholder={t("login.email")}
                autoComplete="email"
                className={cn(authFieldClassName(!!fieldErrorKey), fieldErrorKey && "pr-10")}
                aria-invalid={!!fieldErrorKey}
                aria-describedby={fieldErrorKey ? "forgot-email-error" : undefined}
              />
            </label>
            {fieldErrorKey && <FieldError id="forgot-email-error">{t(fieldErrorKey)}</FieldError>}
          </div>

          <p className="pt-1 text-sm text-muted-foreground">{t("forgot.help")}</p>

          <button type="submit" disabled={busy} className={authPrimaryClass}>
            {busy ? "…" : t("forgot.submit")}
          </button>
        </form>
      )}
    </AuthLayout>
  );
}
